import {
  Box,
  Flex,
  Group,
  Skeleton,
  Stack,
  ThemeIcon,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { useDebouncedCallback, useDisclosure, useLocalStorage } from "@mantine/hooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ContentLocker } from "../ContentLocker";
import { AuctionsTable } from "./components";
import { convertDateRange } from "./utils";
import { matchesSearchQuery } from "./utils/auctionSearch";

import { TimeframeInput } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { useAuctions } from "@/services/auctions/get-auctions";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { opacityForAccess } from "@/utils/contetLocker";

import type { TimeframeRange } from "@/components/elements/TimeframeInput/types";
import type { Auction, AuctionColumns } from "@/types/auctions";
import type { Currency } from "@/types/currency";
import type { FC } from "react";

const ALL_AUCTIONS_PAGE_SIZE = 999999;

export type AuctionsTableHandlerProps = {
  columns?: AuctionColumns[];
  defaultPageSize?: number;
  getPath?: (auctionId: string) => string;
  namespace?: string;
  status: Auction["status"];
  title?: string;
  showBadgeCounter?: boolean;
  withDataFilter?: boolean;
  withSearchFilter?: boolean;
};

export const AuctionsTableHandler: FC<AuctionsTableHandlerProps> = ({
  columns = ["range_date", "place", "name", "num_cars"],
  defaultPageSize = 10,
  getPath,
  namespace,
  status,
  title,
  withDataFilter = false,
  showBadgeCounter = true,
  withSearchFilter = false,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [search, setSearch] = useState("");
  const [showPlanModal, { open: openPlanModal, close: closePlanModal }] = useDisclosure();
  const [dateRange, setDateRange] = useState<TimeframeRange>();
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });
  const isSearching = search.length > 0;
  const updateSearch = useDebouncedCallback((query: string) => {
    setSearch(query.trim());
    setPage(1);
  }, 200);

  const { data, isLoading } = useAuctions({
    status,
    page: isSearching ? 1 : page,
    pageSize: isSearching ? ALL_AUCTIONS_PAGE_SIZE : pageSize,
    parameters: { range: convertDateRange(dateRange) },
  });

  /* Premium Plans */
  const { data: plans } = usePlansUser();
  const canAccess = plans?.config.ad_listing_analytics || false;
  const sourceAuctions = !canAccess ? data?.results?.slice(0, 5) : data?.results;

  const { auctions, totalAuctions } = useMemo(() => {
    const results = sourceAuctions ?? [];

    if (!isSearching) {
      return { auctions: results, totalAuctions: data?.total ?? 0 };
    }

    const filtered = results.filter((auction) =>
      matchesSearchQuery(auction, search, currency.code, t)
    );
    const start = (page - 1) * pageSize;

    return {
      auctions: filtered.slice(start, start + pageSize),
      totalAuctions: filtered.length,
    };
  }, [sourceAuctions, data?.total, isSearching, search, currency.code, t, page, pageSize]);

  function handlePageSizeChange(nextPageSize: number) {
    setPageSize(nextPageSize);
    setPage(1);
  }

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  return canAccess ? (
    /* Premium Plan */
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        {title ? (
          <Group gap="md">
            {showBadgeCounter && canAccess && (
              <ThemeIcon size="sm" radius="sm" w={36} h={26} p="md" color="primary" variant="light">
                {data?.total || 0}
              </ThemeIcon>
            )}
            <Title order={4}>{title}</Title>
          </Group>
        ) : (
          <Box />
        )}
        {withDataFilter && canAccess && (
          <TimeframeInput
            inputProps={{ miw: 230, placeholder: t("components.AuctionsTableHandler.timeframe") }}
            maxDate={new Date()}
            onChange={(value) => setDateRange(value)}
            presets={[]}
            type="month"
            valueFormat="MMM, YYYY"
          />
        )}
      </Group>
      <Skeleton visible={isLoading}>
        <AuctionsTable
          auctions={auctions}
          columns={columns}
          getPath={getPath}
          isSearching={isSearching}
          namespace={namespace}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          onSearch={withSearchFilter ? updateSearch : undefined}
          page={page}
          pageSize={pageSize}
          totalAuctions={totalAuctions}
        />
      </Skeleton>
    </Stack>
  ) : (
    /* Free Plan */
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        {title ? (
          <Group gap="md">
            {showBadgeCounter && canAccess && (
              <ThemeIcon size="sm" radius="sm" w={36} h={26} p="md" color="primary" variant="light">
                {data?.total || 0}
              </ThemeIcon>
            )}
            <Title order={4}>{title}</Title>
          </Group>
        ) : (
          <Box />
        )}
        {withDataFilter && canAccess && (
          <TimeframeInput
            inputProps={{ miw: 230, placeholder: t("components.AuctionsTableHandler.timeframe") }}
            maxDate={new Date()}
            onChange={(value) => setDateRange(value)}
            presets={[]}
            type="month"
            valueFormat="MMM, YYYY"
          />
        )}
      </Group>
      <Skeleton visible={isLoading}>
        <Box pos="relative" style={{ minHeight: auctions.length === 0 ? 250 : undefined }}>
          <Box style={{ opacity: opacityForAccess(canAccess, isDark) }}>
            <AuctionsTable
              auctions={auctions}
              columns={columns}
              getPath={getPath}
              disableNavigation
              isSearching={isSearching}
              namespace={namespace}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              onSearch={withSearchFilter ? updateSearch : undefined}
              page={page}
              pageSize={pageSize}
              totalAuctions={totalAuctions}
            />
          </Box>

          <Flex
            align="center"
            justify="center"
            pos="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            style={{ zIndex: 10 }}
          >
            <Box
              pos="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              style={() => ({
                pointerEvents: "none",
                zIndex: 9,
                background: isDark
                  ? "linear-gradient(to bottom, rgba(27, 27, 27, 0.2), rgba(25,25,25,0.95))"
                  : "linear-gradient(to top, rgba(255,255,255,0.8), rgba(255, 255, 255, 0.1))",
              })}
            />
            <Box pos="relative" style={{ zIndex: 10 }}>
              <ContentLocker
                openPlanModal={openPlanModal}
                closePlanModal={closePlanModal}
                showPlanModal={showPlanModal}
              />
            </Box>
          </Flex>
        </Box>
      </Skeleton>
    </Stack>
  );
};
